import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TrainingAuthLayout from "@container/layout/training-auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@component/tabs";
import { Button } from "@component/button";
import TrainingPeoplePage from "@page/training-people";
import TrainingReportsPage from "@page/training-reports";
import { BarChart3Icon, UsersIcon, DownloadIcon, Loader2Icon } from "lucide-react";
import { downloadReportsExport } from "@service/training";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

type SubTab = "team" | "employees";

const TRIGGER_CLASS =
    "text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors flex-shrink-0 whitespace-nowrap";

// Reports page merges the former People + Reports pages into one nav target.
// Sub-tab state is mirrored to the URL via `?tab=...` so deep links and the
// browser back button work naturally.
export default function TrainingPerformancePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const rawTab = searchParams.get("tab");
    // Accept legacy "individuals" URL param so old links still work
    const initialTab: SubTab = rawTab === "employees" || rawTab === "individuals" ? "employees" : "team";
    const [tab, setTab] = useState<SubTab>(initialTab);

    useEffect(() => {
        const current = searchParams.get("tab");
        if (current !== tab) {
            const next = new URLSearchParams(searchParams);
            next.set("tab", tab);
            setSearchParams(next, { replace: true });
        }
    }, [tab, searchParams, setSearchParams]);

    return (
        <TrainingAuthLayout>
            <div>
                <div className="mb-6 flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
                        <p className="text-sm text-gray-600 mt-1">
                            How your team and employees are performing across training.
                        </p>
                    </div>
                    <DownloadReportButton />
                </div>

                <Tabs
                    value={tab}
                    onValueChange={(v) => setTab(v as SubTab)}
                    className="w-full rounded-none"
                >
                    <TabsList
                        className={cn(
                            "flex w-full bg-transparent h-auto p-0 border-b border-gray-200",
                            "justify-start rounded-none overflow-x-auto scrollbar-hide mb-4",
                        )}
                    >
                        <TabsTrigger value="team" className={TRIGGER_CLASS}>
                            <BarChart3Icon className="w-3.5 h-3.5" />
                            Team Insights
                        </TabsTrigger>
                        <TabsTrigger value="employees" className={TRIGGER_CLASS}>
                            <UsersIcon className="w-3.5 h-3.5" />
                            Employees
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="team" className="mt-0">
                        <TrainingReportsPage embedded />
                    </TabsContent>
                    <TabsContent value="employees" className="mt-0">
                        <TrainingPeoplePage embedded />
                    </TabsContent>
                </Tabs>
            </div>
        </TrainingAuthLayout>
    );
}

// Kept as its own component so it sits inside TrainingAuthLayout's
// SnackbarProvider and can surface success/error toasts. The BE generates the
// Excel file (one sheet per section); the FE only calls the endpoint and saves
// the returned blob.
function DownloadReportButton() {
    const snackbar = useSnackbar();
    const [exporting, setExporting] = useState(false);

    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            await downloadReportsExport({ format: "xlsx" });
            snackbar.showSuccess("Report exported.");
        } catch (err) {
            catchErrorLog(
                `[training] reports export failed: ${err instanceof Error ? err.message : String(err)}`,
            );
            snackbar.showError("Couldn't export the report. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <Button
            onClick={handleExport}
            disabled={exporting}
            className="flex-shrink-0 mt-1"
        >
            {exporting ? (
                <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
                <DownloadIcon className="w-4 h-4" />
            )}
            {exporting ? "Preparing…" : "Export Excel"}
        </Button>
    );
}
